/* ====================================================================
   NSE Bag Widget — assets/nse-bag-widget.js
   Floating cart pill. Single source of truth for cart state across:
     · this widget
     · theme header cart bubble
     · PCD drawer internal state
   Any /cart/* mutation (NSE add, PCD remove, theme, third-party) triggers
   a full sync.
   ==================================================================== */
(function () {
  'use strict';

  var Bag = {
    el: null,
    cart: null,
    _alwaysVisible: false,

    init: function () {
      this.el = document.getElementById('nse-bag-widget');
      this._alwaysVisible = !!document.querySelector('[data-nse-bag-always]');

      // Avoid the "empty-then-filled" flash: keep widget hidden until
      // /cart.js confirms there's something in the bag (unless the
      // merchant opted into always_visible).
      if (this.el && !this._alwaysVisible) {
        this.el.hidden = true;
        this.el.style.visibility = 'hidden';
      }

      this.installCartFetchHook();
      this.refreshFromCart();
      this.bindEvents();
    },

    /* Intercept EVERY cart mutation (ours, PCD's, theme's, 3rd-party apps')
       so the bag widget + theme header bubble + PCD drawer stay in lockstep.
       Cost is one extra /cart.js request per mutation — acceptable for the
       sync guarantee. */
    installCartFetchHook: function () {
      if (window._nseCartFetchHooked) return;
      window._nseCartFetchHooked = true;

      var origFetch = window.fetch;
      window.fetch = function () {
        var url = arguments[0];
        var str = typeof url === 'string' ? url : (url && url.url) || '';
        var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(str);

        var result = origFetch.apply(this, arguments);
        if (isCartMutation) {
          result.then(function (res) {
            // Skip only on hard failure — a 422 from Shopify would have
            // been rejected and no mutation happened, no need to refresh.
            if (!res || !res.ok) return;
            setTimeout(function () {
              if (window.NseBag) window.NseBag.refreshFromCart();
            }, 40);
          }).catch(function () {});
        }
        return result;
      };
    },

    bindEvents: function () {
      var self = this;

      if (this.el) {
        var openBtn = this.el.querySelector('[data-nse-bag-open]');
        if (openBtn) {
          openBtn.addEventListener('click', function (e) {
            e.preventDefault();
            self.openDrawer();
          });
        }
      }

      // Make the theme's cart icon also open PCD drawer (some devices'
      // default behavior was navigating to /cart).
      document.addEventListener('click', function (e) {
        var cartLink = e.target.closest('#cart-icon-bubble, a[href="/cart"], a[href$="/cart"]');
        if (!cartLink) return;
        if (window.PCD && typeof window.PCD.openDrawer === 'function') {
          e.preventDefault();
          self.openDrawer();
        }
      }, true);

      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) self.refreshFromCart();
      });
    },

    /* Open the PCD drawer, bypassing the NSE-suppression patch. */
    openDrawer: function () {
      if (!window.PCD) { window.location.href = '/cart'; return; }
      window._nseAllowDrawer = true;
      if (typeof window._nseOrigOpenDrawer === 'function') {
        window._nseAllowDrawer = false; // reset; we're calling original
        window._nseOrigOpenDrawer();
      } else if (typeof window.PCD.openDrawer === 'function') {
        window.PCD.openDrawer();
      }
    },

    refreshFromCart: function () {
      var self = this;
      return fetch('/cart.js', {
        headers: { 'Accept': 'application/json', 'X-PCD-Own': '1' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.cart = cart;
          self.render();
          self.updateThemeBubble(cart);
          self.syncAddButtons(cart);
          return cart;
        })
        .catch(function (e) { console.error('[NseBag] refresh failed', e); });
    },

    /** Reflect the real cart on every collection-card Add button:
        - If the button's variant (or any of its product's variants) is in the
          cart, show "✓ Added".
        - If not, revert to the original "Add" label.
        Called on every /cart.js refresh so removing an item in the drawer
        instantly reverts the corresponding card. */
    syncAddButtons: function (cart) {
      var cartVariantIds = {};
      var cartProductIds = {};
      (cart && cart.items || []).forEach(function (it) {
        cartVariantIds[String(it.variant_id || it.id)] = true;
        cartProductIds[String(it.product_id)] = true;
      });

      document.querySelectorAll('[data-nse-add]').forEach(function (btn) {
        var vid = String(btn.dataset.variantId || '');
        var card = btn.closest('.nse-card');
        var pid = card ? String(card.dataset.productId || '') : '';

        // Match on EITHER the currently-selected variant id (direct add) OR the
        // product id (variant-picker add — original selected variant may differ
        // from what was chosen in the picker, but the product is the same).
        var inCart = (vid && cartVariantIds[vid]) || (pid && cartProductIds[pid]);

        if (inCart) {
          if (!btn.classList.contains('is-added')) {
            if (!btn.dataset.nseOrig) btn.dataset.nseOrig = btn.innerHTML;
            btn.classList.add('is-added');
            btn.innerHTML = '<span class="nse-card__add-check" aria-hidden="true">✓</span> Added';
            btn.disabled = false;
          }
        } else {
          if (btn.classList.contains('is-added')) {
            btn.classList.remove('is-added');
            btn.innerHTML = btn.dataset.nseOrig || 'Add';
            btn.disabled = false;
          }
        }
      });
    },

    /* Update Shrine Pro's header cart bubble.
       Clear-and-rebuild each call so concurrent updates (PCD + NSE + theme)
       never leave stale text nodes behind (which was causing "44" on a cart
       of 4 items when both updaters ran against the same DOM). */
    updateThemeBubble: function (cart) {
      var wrap = document.getElementById('cart-icon-bubble');
      if (!wrap) return;
      var bubble = wrap.querySelector('.cart-count-bubble');

      if (cart.item_count === 0) {
        if (bubble) bubble.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        wrap.appendChild(bubble);
      }

      // Full reset — no more stale sibling spans.
      bubble.textContent = '';

      if (cart.item_count < 100) {
        var visibleSpan = document.createElement('span');
        visibleSpan.setAttribute('aria-hidden', 'true');
        visibleSpan.textContent = String(cart.item_count);
        bubble.appendChild(visibleSpan);
      }

      var srSpan = document.createElement('span');
      srSpan.className = 'visually-hidden';
      srSpan.textContent = cart.item_count + ' items in cart';
      bubble.appendChild(srSpan);
    },

    render: function () {
      if (!this.el) return;
      if (!this.cart) return;

      if (this.cart.item_count === 0 && !this._alwaysVisible) {
        this.el.hidden = true;
        this.el.style.visibility = 'hidden';
        return;
      }
      this.el.hidden = false;
      this.el.style.visibility = 'visible';

      // Count label: "N Items"
      var countEl = this.el.querySelector('[data-nse-bag-count]');
      if (countEl) countEl.textContent = this.cart.item_count || 0;

      // Up to 3 overlapping thumbnails of the LAST 3 items added
      var thumbsWrap = this.el.querySelector('[data-nse-bag-thumbs]');
      if (!thumbsWrap) return;

      if (this.cart.item_count === 0) {
        thumbsWrap.innerHTML =
          '<span class="nse-bag-widget__thumb nse-bag-widget__thumb--placeholder">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
              '<line x1="3" y1="6" x2="21" y2="6"/>' +
              '<path d="M16 10a4 4 0 0 1-8 0"/>' +
            '</svg>' +
          '</span>';
        return;
      }

      // Take up to 3 items from the end of the cart (most recent first).
      var items = (this.cart.items || []);
      var slice = items.slice(-3).reverse(); // newest first, visible on top
      var html = '';
      for (var i = 0; i < slice.length; i++) {
        var it = slice[i];
        if (it && it.image) {
          html += '<span class="nse-bag-widget__thumb">' +
                    '<img src="' + this.escape(it.image) + '" alt="' + this.escape(it.title || '') + '">' +
                  '</span>';
        } else {
          html += '<span class="nse-bag-widget__thumb nse-bag-widget__thumb--placeholder"></span>';
        }
      }
      thumbsWrap.innerHTML = html;
    },

    /** Called by nse-collection.js card ADD button.
        Uses /cart/add.js with `sections=` so the updated cart drawer HTML
        comes back in the SAME response — one round-trip, no chained fetches,
        no race between add→refreshCart. */
    addAndUpdate: function (variantId) {
      var self = this;
      var vid = parseInt(variantId, 10);
      if (!vid || isNaN(vid)) {
        return Promise.reject(new Error('Missing variant ID'));
      }

      var body = {
        items: [{ id: vid, quantity: 1 }],
        sections: 'premium-cart-drawer',
        sections_url: window.location.pathname
      };

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-PCD-Own': '1' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify(body)
      })
      .then(function (r) {
        return r.json().then(function (resp) {
          if (!r.ok) {
            var msg = (resp && (resp.description || resp.message)) || ('Add failed (HTTP ' + r.status + ')');
            throw new Error(msg);
          }
          return resp;
        });
      })
      .then(function (resp) {
        // Patch PCD drawer from returned section HTML — no extra fetch.
        self.patchDrawerFromSections(resp);
        // Fire widget state refresh in the background so the caller's
        // promise can resolve immediately (button flips to "Added" fast).
        self.refreshFromCart().then(function () { self.bump(); }).catch(function(){});
        return resp;
      });
    },

    /** Replace the PCD cart drawer contents from a /cart/add.js `sections`
        response so when the user taps the bag widget, the drawer is already
        current — no full-page refresh needed. */
    patchDrawerFromSections: function (resp) {
      if (!resp || !resp.sections) return;
      var html = resp.sections['premium-cart-drawer'];
      if (!html) return;
      var drawer = document.getElementById('pcd-drawer');
      if (!drawer) return;

      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newDrawer = doc.getElementById('pcd-drawer');
        if (!newDrawer) return;

        // Preserve scroll so an open drawer doesn't snap
        var oldScrollEl = drawer.querySelector('#pcd-scroll');
        var savedTop = oldScrollEl ? oldScrollEl.scrollTop : 0;
        var wasOpen = drawer.classList.contains('is-open');

        drawer.innerHTML = newDrawer.innerHTML;

        // Re-assert open state / scroll pos
        if (wasOpen) drawer.classList.add('is-open');
        var newScrollEl = drawer.querySelector('#pcd-scroll');
        if (newScrollEl && savedTop) newScrollEl.scrollTop = savedTop;

        // Let PCD know it needs to re-bind (it uses MutationObserver in newer
        // builds, but this is belt-and-braces).
        if (window.PCD) {
          if (typeof window.PCD.clearStaleCouponIfAny === 'function') {
            try { window.PCD.clearStaleCouponIfAny(); } catch (e) {}
          }
          if (typeof window.PCD.shuffleUpsell === 'function') {
            try { window.PCD.shuffleUpsell(); } catch (e) {}
          }
        }
        document.dispatchEvent(new CustomEvent('pcd:refreshed'));
      } catch (e) {
        console.error('[NseBag] patchDrawerFromSections:', e);
      }
    },

    bump: function () {
      if (!this.el) return;
      this.el.classList.remove('nse-bag-widget--bumped');
      void this.el.offsetWidth;
      this.el.classList.add('nse-bag-widget--bumped');
    },

    /** Optimistic: un-hide the widget, increment the Items count by 1.
        Gets reconciled against the real cart shortly after by refreshFromCart. */
    optimisticIncrement: function () {
      if (!this.el) return;
      this.el.hidden = false;
      this.el.style.visibility = 'visible';

      var countEl = this.el.querySelector('[data-nse-bag-count]');
      if (countEl) {
        var n = parseInt(countEl.textContent, 10);
        if (isNaN(n)) n = 0;
        countEl.textContent = n + 1;
      }
      this.bump();
    },

    escape: function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
  };

  window.NseBag = Bag;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Bag.init(); });
  } else {
    Bag.init();
  }
})();
